import { useCallback, useState, useRef, FC } from "react";
import { useS3Upload } from "@src/features/app/hooks/useS3Upload";
import { Button } from "@src/lib/ui/Button";
import { PresignedS3PostDataBucket } from "@src/types.generated";
import UploadIcon from "@src/lib/icons/18px/arrow-upload";
import { useToast } from "@src/lib/hooks/useToast";

type Props = {
  onUploadComplete: (s3Key: string, packageName: string) => void;
  buttonText?: string;
};

const ScormUploadButton: FC<Props> = ({
  onUploadComplete,
  buttonText = "Upload .zip file",
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const { addErrorToast } = useToast();

  const { handleFileUpload, isUploading } = useS3Upload({
    onUploadProgress: (_, progress) => {
      setUploadProgress(progress);
    },
    onUploadComplete: (upload) => {
      onUploadComplete(upload.key, upload.name);
    },
    onUploadFailed: () => {
      addErrorToast({
        title: "Failed to upload file; please try again.",
      });
    },
    bucket: PresignedS3PostDataBucket.Scorm,
    enforceFileSizeLimit: false,
  });

  const handleFileSelection = useCallback(
    (selectedFile: File) => {
      handleFileUpload(selectedFile, "scorm-upload");
    },
    [handleFileUpload],
  );

  const openFilePicker = useCallback(() => {
    inputRef.current?.click();
  }, []);

  return (
    <>
      <Button variant="outline" onClick={openFilePicker} disabled={isUploading}>
        <UploadIcon className="size-4" />
        {isUploading ? `Uploading ${uploadProgress}%...` : buttonText}
      </Button>
      <input
        ref={inputRef}
        type="file"
        accept=".zip"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleFileSelection(file);
        }}
      />
    </>
  );
};

export default ScormUploadButton;
